import Stack from '@mui/material/Stack';
import { FC, useContext, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import { COMMON_STRING, DICTIONARY } from '@/constants/dictionary';
import { useScorerInfoConfig } from '@/service/hooks/useScorerInfo';
import { ScoringContext } from '@/contexts/scoring/context';
import { ScorerInfoConfig } from '@/service/types';
import { formatDate } from '@/utils/formatDate/formatDate';
import { TIME_FORMAT } from '@/constants/dateFormats';
import { ScorerPhotoDialog } from '../ScorerPhotoDialog/ScorerPhotoDialog';
import { ScorerInfo } from './ScorersInfo';

export interface ScorersPanelProps {
  isOpen: boolean;
}

export const ScorersPanel: FC<ScorersPanelProps> = ({ isOpen }) => {
  const [matchScorers, setMatchScorers] = useState<ScorerInfoConfig[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { scorerConfig } = useScorerInfoConfig();
  const {
    state: {
      fixtureId,
      scoringAppInfo,
      dataCollectionResourses,
      dataCollectorsBatteryLevel,
    },
  } = useContext(ScoringContext);

  useEffect(() => {
    if (!dataCollectionResourses) {
      return;
    }
    setMatchScorers(dataCollectionResourses);
  }, [dataCollectionResourses]);

  useEffect(() => {
    if (!scorerConfig || !scorerConfig.length) {
      return;
    }

    setMatchScorers(scorerConfig);
  }, [scorerConfig]);

  useEffect(() => {
    if (!scoringAppInfo || !matchScorers.length) {
      return;
    }

    const { scorerId, scoringAppVersion, scoringAppType, deviceId } =
      scoringAppInfo;

    setMatchScorers(
      (prev) =>
        prev.map((scorer) =>
          scorer.oktaId === scorerId
            ? {
                ...scorer,
                applicationInformation: {
                  scorerId,
                  scoringAppVersion,
                  scoringAppType,
                  deviceId,
                },
              }
            : scorer,
        ) as ScorerInfoConfig[],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoringAppInfo]);

  useEffect(() => {
    if (!isOpen || !dataCollectorsBatteryLevel || !matchScorers.length) {
      return;
    }

    setMatchScorers(
      (prev) =>
        prev.map((scorer) =>
          scorer.oktaId === dataCollectorsBatteryLevel.scorerId
            ? {
                ...scorer,
                scoringApplicationBatteryLevel: {
                  ...dataCollectorsBatteryLevel,
                },
              }
            : scorer,
        ) as ScorerInfoConfig[],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCollectorsBatteryLevel]);

  const handleAvatarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    const avatar = target.closest<HTMLElement>('[data-role="scorer-avatar"]');

    if (!avatar) return;

    const userId = avatar.dataset.userId;
    if (!userId) return;

    setSelectedUserId(userId);
  };

  const selectedScorer = matchScorers.find((s) => s.userId === selectedUserId);

  if (!matchScorers?.length)
    return (
      <Paper>
        <Collapse in={isOpen}>
          <Stack width='100%' direction='row' flexWrap='wrap'>
            <ScorerInfo
              firstName={COMMON_STRING.UNASSIGNED}
              lastName={COMMON_STRING.UNASSIGNED}
              lastDataCollectionDate={COMMON_STRING.NOT_AVAILABLE}
            />
          </Stack>
        </Collapse>
      </Paper>
    );

  return (
    <>
      <Paper>
        <Collapse in={isOpen}>
          <Stack
            onClick={handleAvatarClick}
            width='100%'
            direction={matchScorers.length >= 3 ? 'column' : 'row'}
            flexWrap='wrap'
          >
            {!fixtureId
              ? DICTIONARY.ERROR.FIXTURE_ID_MISSING
              : matchScorers.map(
                  ({
                    userId,
                    firstName,
                    lastName,
                    averageFor5LastFixtures,
                    numberOfCompletedMatches,
                    lastDataCollectionDate,
                    applicationInformation,
                    scoringApplicationBatteryLevel,
                    photo,
                  }: ScorerInfoConfig) => (
                    <ScorerInfo
                      key={userId}
                      firstName={firstName}
                      lastName={lastName}
                      lastDataCollectionDate={
                        lastDataCollectionDate
                          ? formatDate(
                              lastDataCollectionDate,
                              TIME_FORMAT.DATE_FORMAT,
                            )
                          : COMMON_STRING.NOT_AVAILABLE
                      }
                      averageFor5LastFixtures={averageFor5LastFixtures}
                      numberOfCompletedMatches={numberOfCompletedMatches}
                      applicationInformation={applicationInformation}
                      photo={photo}
                      userId={userId}
                      scoringApplicationBatteryLevel={
                        scoringApplicationBatteryLevel
                      }
                    />
                  ),
                )}
          </Stack>
        </Collapse>
      </Paper>
      <ScorerPhotoDialog
        open={!!selectedScorer?.photo}
        photo={selectedScorer?.photo || ''}
        fullName={
          selectedScorer
            ? `${selectedScorer.firstName} ${selectedScorer.lastName}`
            : ''
        }
        onClose={() => setSelectedUserId(null)}
      />
    </>
  );
};
